/*
  The engraving bag fix (decision L5): each engraving fee stays with its Resono.

  The engraving switch (assets/rayvox.js, engrave()) puts the fee and its Resono in the bag as two lines that share a
  hidden line item property, _engraving_id. Hidden properties (a leading underscore) don't show in the bag or at
  checkout, but do on the order in the admin, so the two lines can be matched there too. After every change to the
  bag that announces itself with Shopify's cart event (Horizon's drawer and cart page, Add to Bag, the cart add-on),
  this reads the bag and holds each fee line to its Resono:
  - removing the Resono removes its fee, and lowering the Resono's quantity lowers the fee to match
  - a fee line can't go above its Resono's quantity: its + stops there in the drawer and on the cart page, and a
    higher number typed in, or sent some other way, is brought back down
  - a fee is never raised by itself: more Resonos than fees means some are plain, and charging for another
    engraving needs the buyer's own click
  Fee lines without the link (put in the bag some other way) are held, together, to the bag's Resonos that have no
  link, and go when there are none. An order that still has a fee without its Resono is caught by the Shopify Flow
  alert in the go-live runbook (docs/runbooks/go-live.md).

  No Horizon file is edited. This listens for the cart event Horizon's own components send, and after a fix sends
  one itself with the re-rendered drawer and cart page, so they redraw as they do after any other change. A redraw
  replaces the quantity inputs, so the + limit is put back after each one.
*/

const FEE = 'engraving';
const LINK = '_engraving_id';
const SOURCE = 'rayvox-bag';

const routes = () => window.Theme?.routes || {};
const isFee = (item) => item.handle === FEE;
// A Resono that can be engraved: the Go, the Pro v2 and the older Resonos, but not the Flex.
const isResono = (item) => !isFee(item) && /^resono(?!-flex)/.test(item.handle || '');

/**
 * For the bag's items: the quantity each fee line has to come down to (fixes), and the most each fee line may have
 * (caps), both by line key.
 */
export function plan(items) {
  const left = new Map(); // link id ('' for no link) → Resonos not yet matched by a fee
  for (const item of items) {
    if (isFee(item)) continue;
    const id = item.properties?.[LINK] || '';
    if (id || isResono(item)) left.set(id, (left.get(id) || 0) + item.quantity);
  }
  const fixes = new Map();
  const kept = [];
  for (const item of items) {
    if (!isFee(item)) continue;
    const id = item.properties?.[LINK] || '';
    const keep = Math.min(item.quantity, left.get(id) || 0);
    left.set(id, (left.get(id) || 0) - keep);
    if (keep !== item.quantity) fixes.set(item.key, keep);
    kept.push([item.key, id, keep]);
  }
  const caps = new Map(kept.map(([key, id, keep]) => [key, keep + (left.get(id) || 0)]));
  return { fixes, caps };
}

let caps = new Map();

/** Puts each fee line's limit on its quantity input in the drawer and on the cart page. */
function paint() {
  for (const row of document.querySelectorAll('cart-items-component tr[data-key]')) {
    const cap = caps.get(row.dataset.key);
    if (!cap) continue;
    const input = row.querySelector('input[data-cart-line]');
    const selector = input?.closest('cart-quantity-selector-component');
    if (!input || !selector) continue;
    if (input.max !== String(cap)) input.max = String(cap);
    selector.updateButtonStates?.();
  }
}

let queued = false;
const observer = new MutationObserver(() => {
  if (queued) return;
  queued = true;
  queueMicrotask(() => {
    queued = false;
    paint();
  });
});
const watched = new WeakSet();
function watch() {
  for (const el of document.querySelectorAll('cart-items-component')) {
    if (watched.has(el)) continue;
    watched.add(el);
    observer.observe(el, { childList: true, subtree: true, attributes: true, attributeFilter: ['max'] });
  }
}

const events = () => import('@shopify/events');

/** Lowers the fee lines in fixes (line key → quantity) and tells Horizon, with the re-rendered sections. */
async function fix(fixes) {
  const { CartLinesUpdateEvent } = await events();
  const sections = [
    ...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map((el) => el.dataset.sectionId)),
  ];
  const done = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'remove',
      context: 'cart',
      lines: [...fixes].map(([id, quantity]) => ({ id, quantity })),
      promise: done.promise,
    })
  );
  try {
    const body = { updates: Object.fromEntries(fixes) };
    if (sections.length) Object.assign(body, { sections: sections.join(','), sections_url: location.pathname });
    const res = await fetch(`${routes().cart_update_url || '/cart/update'}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const cart = await res.json();
    if (!res.ok || cart.status) throw new Error(cart.description || cart.message || 'The bag couldn’t be updated.');
    caps = plan(cart.items).caps;
    done.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        sections: cart.sections || undefined,
        items: cart.items,
        itemCount: cart.item_count,
        source: SOURCE,
        didError: false,
      },
    });
  } catch (error) {
    done.reject(error);
  }
}

let running = null;
let again = false;

/** Reads the bag and fixes any fee line that's more than its Resono. Calls made while it runs make it run once more. */
function sync() {
  if (running) {
    again = true;
    return running;
  }
  running = (async () => {
    do {
      again = false;
      // Let Horizon's own quantity changes finish first.
      const pending = customElements.get('cart-items-component')?.pendingQuantityUpdates;
      if (pending?.length) await Promise.allSettled(pending);
      const cart = await fetch(`${routes().cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null);
      if (!cart?.items) return;
      const next = plan(cart.items);
      caps = next.caps;
      watch();
      paint();
      if (next.fixes.size) await fix(next.fixes);
    } while (again);
  })()
    .catch((error) => console.warn('[rayvox-bag]', error))
    .finally(() => (running = null));
  return running;
}

const hasFee = () => !!document.querySelector(`cart-items-component a[href*="/products/${FEE}"]`);

events()
  .then(({ StandardEvents }) => {
    document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
      watch();
      event.promise
        ?.then((result) => result?.detail?.source !== SOURCE && sync())
        .catch(() => sync());
    });
  })
  .catch((error) => console.warn('[rayvox-bag] Shopify’s cart events didn’t load', error));

watch();
if (hasFee()) sync();
addEventListener('pageshow', (e) => e.persisted && hasFee() && sync());
